import type { Contract } from '../types';
import { agreementVariationNumber } from './agreementIdentity';

/** A signed ÄTA and what it changed the project by, after ROT. */
export interface VariationChange {
  contractId: string;
  /** The ÄTA number as shown everywhere else ("ÄTA #2"). */
  number: number | null;
  /** Positive when the project grows, negative when it gets cheaper. */
  deltaOre: number;
  signedAt: string;
}

const signedAt = (contract: Contract): string | null => {
  if (!contract.companySigned || !contract.personSigned) return null;
  if (contract.signingCompletedAt) return contract.signingCompletedAt;
  const times = [contract.companySignedAt, contract.personSignedAt].filter((time): time is string => Boolean(time));
  return times.sort().at(-1) ?? null;
};

/**
 * Every signed ÄTA, oldest first (ClickUp 869fnvpvp): a scoped amendment
 * changes the project by its delta, a separate ÄTA contract by its own value,
 * which is negative for a price reduction. Other contract versions, such as a
 * replacement agreement after a conflict, are not ÄTAs.
 */
export function variationChanges(contracts: Contract[]): VariationChange[] {
  return contracts
    .flatMap(contract => {
      const at = signedAt(contract);
      if (!at) return [];
      if (contract.stageVariation) {
        return [{ contractId: contract.id, number: agreementVariationNumber(contract), deltaOre: contract.stageVariation.afterRotDeltaOre, signedAt: at }];
      }
      if (contract.contractType !== 'variation') return [];
      return [{ contractId: contract.id, number: agreementVariationNumber(contract), deltaOre: Math.round(Number(contract.afterRotAmount) * 100), signedAt: at }];
    })
    .filter(change => Number.isFinite(change.deltaOre))
    .sort((a, b) => a.signedAt.localeCompare(b.signedAt));
}

/** The most recently signed ÄTA, or null. */
export const latestVariationChange = (contracts: Contract[]): VariationChange | null =>
  variationChanges(contracts).at(-1) ?? null;
